import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { memo } from 'react'

import Button from '@/components/base/Button'
import Simple24hLineChart from '@/components/chart/simple-24h-line-chart'
import useTrans from '@/hooks/useTrans'
import type { CustomTokenInfo } from '@/utils/helpers'
import { format } from '@/utils/numeric'

import { ACTION_TYPE, COLUMNS_KEY, getColorStyle } from '../../helpers'
import type { TopCoinsDataItem, ViewPropsCommon } from '../../types'
import ViewTokenName from '../../view.token-name'
import { StyledTableCell, StyledTableRow } from '../styles'

type Props = {
  index: number
  data: TopCoinsDataItem
  tokenA?: CustomTokenInfo
  tokenB?: CustomTokenInfo
  columns: ViewPropsCommon['columns']
  onClickTokenItem: ViewPropsCommon['onClickTokenItem']
}

const View = ({ tokenA, tokenB, columns, data, index, onClickTokenItem }: Props) => {
  const trans = useTrans()

  return (
    <StyledTableRow
      onClick={() => onClickTokenItem(data)}
      className="cursor-pointer"
      data-testid={`top-coins-table-item-${data.symbol}`}
    >
      {columns.map((col) => {
        switch (col.key) {
          case COLUMNS_KEY.index:
            return (
              <StyledTableCell>
                <Typography className="text-sm text-[#C2CBD9]">{index + 1}</Typography>
              </StyledTableCell>
            )
          case COLUMNS_KEY.balances:
            return <StyledTableCell>{data.userBalance}</StyledTableCell>
          case COLUMNS_KEY.volume24h:
            return <StyledTableCell>{data.volume24h}</StyledTableCell>
          case COLUMNS_KEY.tokenName:
            return (
              <StyledTableCell>
                <ViewTokenName data={data} />
              </StyledTableCell>
            )
          case COLUMNS_KEY.marketCap:
            return <StyledTableCell>{data.marketCapFormatted}</StyledTableCell>
          case COLUMNS_KEY.holders:
            return <StyledTableCell>{data.holders}</StyledTableCell>
          case COLUMNS_KEY.diff24Hours:
            return (
              <StyledTableCell>
                <Typography
                  className="text-sm font-[500]"
                  style={getColorStyle(data.diff24Hours)}
                  data-testid="percent-24-hours"
                >
                  {data.diff24Hours ? format(data.diff24Hours, { sign: true }) : '0'}%
                </Typography>
              </StyledTableCell>
            )
          case COLUMNS_KEY.chart:
            return (
              <StyledTableCell>
                {tokenA && tokenB && data.isHasChart && (
                  <Box className="relative min-w-[140px] w-[140px] h-[36px]">
                    <Box className="absolute inset-0">
                      <Simple24hLineChart
                        options={{ candleWindowSize: 168, timeFrame: 'one_hour' }}
                        tokenA={tokenA}
                        tokenB={tokenB}
                      />
                    </Box>
                  </Box>
                )}
              </StyledTableCell>
            )
          case COLUMNS_KEY.actionsSpot:
            return (
              <StyledTableCell>
                <Stack justifyContent="flex-end" direction="row" gap="12px">
                  <Button
                    data-testid={`${data.symbol}-withdraw-btn`}
                    onClick={(evt) => {
                      evt.stopPropagation()
                      col.onClick?.(data, ACTION_TYPE.withdraw)
                    }}
                    className="min-w-[90px]"
                    variant="outlined"
                    size="sm"
                  >
                    {trans.common.withdraw}
                  </Button>
                  <Button
                    data-testid={`${data.symbol}-deposit-btn`}
                    onClick={(evt) => {
                      evt.stopPropagation()
                      col.onClick?.(data, ACTION_TYPE.deposit)
                    }}
                    className="min-w-[90px]"
                    variant="primary"
                    size="sm"
                    disabled
                  >
                    {trans.common.deposit}
                  </Button>
                </Stack>
              </StyledTableCell>
            )
          default:
            return null
        }
      })}
    </StyledTableRow>
  )
}

export default memo(View)
